import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import EditPositionModal from '../components/modals/EditPositionModal';
import EditDepartmentModal from '../components/modals/EditDepartmentModal';
import CreatePositionModal from '../components/modals/CreatePositionModal';
import CreateDepartmentModal from '../components/modals/CreateDepartmentModal';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import { Position, Department } from '../types/user';
import { getPositions, getDepartments, deletePosition, deleteDepartment, updatePosition, updateDepartment, createPosition, createDepartment } from '../services/userApi';

export default function PositionsDepartmentsPage() {
    // State for positions and departments
    const [positions, setPositions] = useState<Position[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Edit modal states
    const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
    const [isEditPositionModalOpen, setIsEditPositionModalOpen] = useState(false);
    const [isEditDepartmentModalOpen, setIsEditDepartmentModalOpen] = useState(false);
    
    // Create modal states
    const [isCreatePositionModalOpen, setIsCreatePositionModalOpen] = useState(false);
    const [isCreateDepartmentModalOpen, setIsCreateDepartmentModalOpen] = useState(false);
    
    // Delete modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteType, setDeleteType] = useState<'position' | 'department'>('position');
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);

    useEffect(() => {
        fetchPositionsAndDepartments();
    }, []);

    const fetchPositionsAndDepartments = async () => {
        setIsLoading(true);
        try {
            const [positionsRes, departmentsRes] = await Promise.all([
                getPositions(),
                getDepartments()
            ]);
            setPositions(positionsRes.data);
            setDepartments(departmentsRes.data);
        } catch (error) {
            showErrorToast('Failed to fetch positions and departments');
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditPosition = (position: Position) => {
        setSelectedPosition(position);
        setIsEditPositionModalOpen(true);
    };

    const handleEditDepartment = (department: Department) => {
        setSelectedDepartment(department);
        setIsEditDepartmentModalOpen(true);
    };

    const handleDeletePosition = (positionId: number) => {
        setDeleteType('position');
        setItemToDelete(positionId);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteDepartment = (departmentId: number) => {
        setDeleteType('department');
        setItemToDelete(departmentId);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        try {
            if (deleteType === 'position') {
                await deletePosition(itemToDelete);
                showSuccessToast('Position deleted successfully');
            } else {
                await deleteDepartment(itemToDelete);
                showSuccessToast('Department deleted successfully');
            }
            fetchPositionsAndDepartments();
        } catch (error) {
            showErrorToast(`Failed to delete ${deleteType}`);
            console.error(`Error deleting ${deleteType}:`, error);
        } finally {
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
        }
    };

    const handleUpdatePosition = async (id: number, data: { name: string; department_id: number }) => {
        try {
            await updatePosition(id, data);
            showSuccessToast('Position updated successfully');
            fetchPositionsAndDepartments();
        } catch (error) {
            showErrorToast('Failed to update position');
            console.error('Error updating position:', error);
        }
    };

    const handleUpdateDepartment = async (id: number, data: { name: string }) => {
        try {
            await updateDepartment(id, data);
            showSuccessToast('Department updated successfully');
            fetchPositionsAndDepartments();
        } catch (error) {
            showErrorToast('Failed to update department');
            console.error('Error updating department:', error);
        }
    };

    const handleCreatePosition = async (data: { name: string; department_id: number }) => {
        try {
            await createPosition(data);
            showSuccessToast('Position created successfully');
            fetchPositionsAndDepartments();
        } catch (error) {
            showErrorToast('Failed to create position');
            console.error('Error creating position:', error);
        }
    };

    const handleCreateDepartment = async (data: { name: string }) => {
        try {
            await createDepartment(data);
            showSuccessToast('Department created successfully');
            fetchPositionsAndDepartments();
        } catch (error) {
            showErrorToast('Failed to create department');
            console.error('Error creating department:', error);
        }
    };

    return (
        <div className="space-y-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Positions & Departments</h1>
            
            {isLoading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Positions Section */}
                    <section className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Positions</h2>
                            <button
                                onClick={() => setIsCreatePositionModalOpen(true)}
                                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                            >
                                <Plus size={16} />
                                <span>Add Position</span>
                            </button>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-700">
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Department</th>
                                        <th scope="col" className="relative px-4 py-3">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {positions.map((position) => (
                                        <tr key={position.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{position.name}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                {position.department?.name || 'No Department'}
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEditPosition(position)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeletePosition(position.id)}
                                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {positions.length === 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                                                No positions found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Departments Section */}
                    <section className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Departments</h2>
                            <button
                                onClick={() => setIsCreateDepartmentModalOpen(true)}
                                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                            >
                                <Plus size={16} />
                                <span>Add Department</span>
                            </button>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-700">
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                        <th scope="col" className="relative px-4 py-3">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {departments.map((department) => (
                                        <tr key={department.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{department.name}</td>
                                            <td className="px-4 py-3 text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEditDepartment(department)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteDepartment(department.id)}
                                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {departments.length === 0 && (
                                        <tr>
                                            <td colSpan={2} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                                                No departments found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}

            {/* Modals */}
            {/* Edit Modals */}
            {selectedPosition && (
                <EditPositionModal
                    isOpen={isEditPositionModalOpen}
                    onClose={() => setIsEditPositionModalOpen(false)}
                    onSubmit={handleUpdatePosition}
                    position={selectedPosition}
                />
            )}

            {selectedDepartment && (
                <EditDepartmentModal
                    isOpen={isEditDepartmentModalOpen}
                    onClose={() => setIsEditDepartmentModalOpen(false)}
                    onSubmit={handleUpdateDepartment}
                    department={selectedDepartment}
                />
            )}

            {/* Create Modals */}
            <CreatePositionModal
                isOpen={isCreatePositionModalOpen}
                onClose={() => setIsCreatePositionModalOpen(false)}
                onSubmit={handleCreatePosition}
            />

            <CreateDepartmentModal
                isOpen={isCreateDepartmentModalOpen}
                onClose={() => setIsCreateDepartmentModalOpen(false)}
                onSubmit={handleCreateDepartment}
            />

            {/* Delete Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                itemType={deleteType}
                itemName={
                    deleteType === 'position'
                        ? positions.find(p => p.id === itemToDelete)?.name || ''
                        : departments.find(d => d.id === itemToDelete)?.name || ''
                }
            />
        </div>
    );
} 